import { useContext, useEffect, useState } from 'react'
import { IconButton } from '@mui/material'
import CustomOutput from '../../components/TextOutput'
import CustomButton from '../../components/Button'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CustomModal from '../../containers/Modal'
import EducationForm from './EducationForm'
import WorkForm from './WorkForm'
import { TEducationalInfo, TProfessionalInfo } from '../../hooks/useFormRegister'
import { RegisterContext } from '../../context/RegisterContext'
import { RegisterFormInfoTypes } from '../../helpers/enums'
import { AppConfig } from '../../models/shared'
import UtilitiesService from '../../services/UtilitiesService'
import { formatDateToOnlyMonthAndYear } from '../../helpers/utils'

interface Props<T> {
    title: string
    infoArray: T[]
    type: RegisterFormInfoTypes.Educational | RegisterFormInfoTypes.Professional
    dateRef?: string
}

function FormList<T extends TProfessionalInfo | TEducationalInfo>({
    type,
    title,
    infoArray,
    dateRef,
}: Props<T>) {
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
    const [selectedId, setSelectedId] = useState<string>()
    const [config, setConfig] = useState<AppConfig>()
    const { deleteInfo } = useContext(RegisterContext)

    useEffect(() => {
        if (infoArray.length > 0) return
        new UtilitiesService().getAppConfig().then((response) => {
            if (response.status !== 200) return
            setConfig(response.data)
        })
    }, [infoArray])

    const handleAddItem = () => {
        setSelectedId(undefined)
        setIsFormOpen(true)
    }

    const handleEditItem = (id: string) => {
        setSelectedId(id)
        setIsFormOpen(true)
    }

    return (
        <div className="flex flex-col gap-2 md:gap-0">
            <span className="md:mt-4 text-base md:text-xl">{title}</span>
            {infoArray.length > 0 && (
                <>
                    {infoArray.map((info, index) => (
                        <div
                            key={`${type}${info.id}`}
                            className={`grid ${type !== RegisterFormInfoTypes.Educational || index > 0 ? 'grid-cols-[auto_60px] md:grid-cols-[auto_100px]' : 'grid-cols-[auto_25px] md:grid-cols-[auto_50px]'} gap-3 md:gap-6 md:mt-3 items-center`}
                        >
                            <CustomOutput
                                key={info.id}
                                primaryText={
                                    ((info as TProfessionalInfo).companyName as string) ??
                                    ((info as TEducationalInfo).school as string)
                                }
                                secondaryText={`${formatDateToOnlyMonthAndYear(info?.startDate ?? '')} - ${info.endingDate !== '' ? formatDateToOnlyMonthAndYear(info?.endingDate ?? '') : 'Actualidad'}`}
                                className="pointer-events-none"
                            />
                            <div className="w-fit flex md:gap-4">
                                <IconButton
                                    className="p-1 md:p-2"
                                    onClick={() => handleEditItem(info.id)}
                                >
                                    <EditIcon />
                                </IconButton>
                                {(type !== RegisterFormInfoTypes.Educational || index > 0) && (
                                    <IconButton
                                        className="p-1 md:p-2"
                                        onClick={() => deleteInfo(type, info.id)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </div>
                        </div>
                    ))}
                </>
            )}
            <CustomButton
                text={`Agregar ${title}`}
                className="w-full md:mt-3 h-[36px] text-xs md:text-base"
                onClick={handleAddItem}
                startIcon={<AddCircleOutlineIcon />}
                secondary
                disabled={dateRef === ''}
            />
            <CustomModal
                isOpen={isFormOpen}
                handleClose={() => setIsFormOpen(false)}
            >
                {type === RegisterFormInfoTypes.Educational ? (
                    <EducationForm
                        handleClose={() => setIsFormOpen(false)}
                        id={selectedId}
                        dateRef={dateRef as string}
                        config={infoArray.length === 0 || selectedId === '1' ? config : undefined}
                    />
                ) : (
                    <WorkForm
                        handleClose={() => setIsFormOpen(false)}
                        id={selectedId}
                        dateRef={dateRef as string}
                    />
                )}
            </CustomModal>
        </div>
    )
}

export default FormList
